import React, { useEffect, useState } from "react";
import { Quotes, Star } from "@phosphor-icons/react";

/**
 * Product-page testimonials. Slug-keyed so each product tells the right story;
 * a generic set is used when no slug-specific set exists.
 */
const BY_SLUG = {
  "bank-reconciliation-suite": [
    { q: "The nostro recon that used to run three days now closes before lunch. Break-fixes down to zero this quarter.", who: "Head of Ops", role: "Private bank · Harare, Zimbabwe" },
    { q: "Rolled it out to two subsidiaries in six weeks. The onboarding team knew our regulator forms better than we did.", who: "CFO", role: "Financial services · UAE" },
  ],
  "hospital-clinic-hms": [
    { q: "MedFlow paid for itself within one quarter. The claims team feels three times faster.", who: "Rahman N.", role: "COO, Wellcare Group · Dubai" },
    { q: "Reminders alone cut our no-shows by 10 points. Whoever wrote the parent app understood our patients.", who: "Medical Director", role: "OPD chain · Bengaluru, India" },
  ],
  "school-college-erp": [
    { q: "Parent engagement 3× overnight — the new parent app is genuinely better than the multinational one we retired.", who: "Karan M.", role: "Director, Nexus Academy · Bengaluru" },
    { q: "The bus tracking module alone justified the switch. Parents finally trust the timing.", who: "Principal", role: "K-12 campus · Lusaka, Zambia" },
  ],
  "hotel-management-system": [
    { q: "Front desk uses one screen now. Occupancy and revenue on the same page — we haven't opened a spreadsheet in months.", who: "GM", role: "Boutique hotel · Dubai" },
    { q: "Went live between two seasons — the migration didn't cost us a single booking.", who: "Owner", role: "Beach resort · Zanzibar" },
  ],
  "manufacturing-erp": [
    { q: "MRP + shop-floor terminals dropped our raw-material wastage from 18% to 6% inside a year.", who: "Plant Head", role: "Copper-belt manufacturer · Zambia" },
    { q: "Their team understood our ISO audit calendar better than the old vendor did in ten years.", who: "QA Manager", role: "Auto-components maker · Pune, India" },
  ],
  "logistics-fleet": [
    { q: "On-time delivery went from 78% to 96%. Drivers stopped calling dispatch — the app is enough.", who: "COO", role: "Cross-border logistics · Lagos, Nigeria" },
    { q: "Fuel reconciliation was our biggest leak. Now it's an exception report, not a monthly investigation.", who: "Fleet Head", role: "Distributor · Gaborone, Botswana" },
  ],
};

const GENERIC = [
  { q: "The pricing was fixed, the go-live date held, and the software actually worked on day one.", who: "Founder", role: "Growth-stage brand · UAE" },
  { q: "One partner for the product, the hosting and the growth — no finger-pointing when something breaks.", who: "COO", role: "Regional retailer · India" },
];

export default function ProductTestimonials({ productSlug }) {
  const items = BY_SLUG[productSlug] || GENERIC;
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setI((v) => (v + 1) % items.length), 6500);
    return () => clearInterval(id);
  }, [items.length]);
  const q = items[i];
  return (
    <section data-testid="product-testimonials" className="mt-14 border border-[color:var(--line)] bg-[color:var(--paper-alt)] p-6 md:p-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="eyebrow">What clients say</div>
        <div className="flex items-center gap-1" data-testid="product-testimonials-rating">
          {[1,2,3,4,5].map((n) => <Star key={n} size={13} weight="fill" className="text-[color:var(--brand)]" />)}
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600 ml-2">4.9 · verified rollouts</span>
        </div>
      </div>
      <div key={i} className="quote-fade mt-4 max-w-3xl">
        <Quotes size={22} weight="fill" className="text-[color:var(--brand)]" />
        <p className="mt-3 font-display text-xl md:text-2xl leading-snug text-neutral-800">&ldquo;{q.q}&rdquo;</p>
        <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-neutral-500">— {q.who} · {q.role}</div>
      </div>
      {items.length > 1 && (
        <div className="mt-5 flex gap-2">
          {items.map((_, idx) => (
            <button
              key={idx}
              data-testid={`product-testimonial-dot-${idx}`}
              onClick={() => setI(idx)}
              aria-label={`Show testimonial ${idx + 1}`}
              className={`h-1 transition-all ${idx === i ? "bg-[color:var(--brand)] w-8" : "bg-neutral-300 w-3 hover:bg-neutral-400"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
